import { useCallback, useEffect, useState } from "react";
import { TypedDocumentString } from "src/graphql/generated/graphql";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../../../../utils/error";

// Every shipped copy of the campaign's reviews, from Campaign.reviewDeliveries — the products
// table's shipped index, paginated as {destination, reviewId, lang, origin}. We page to completion
// and drop exact duplicates; the report (derive.ts) then keys each copy per destination × (main |
// translation tenant) and counts distinct reviews per key, so a translated copy (origin
// "translation", lang of a translation tenant) no longer folds into the main bar (TTV2-1559).
// This is the real shipped signal for the e-shop funnel bars. Paginated on purpose:
// pre-aggregating server-side truncated large campaigns at DynamoDB's 1 MB page limit.
export interface ReviewDelivery {
    destination: string;
    reviewId: string;
    lang: string | null;
    origin: string | null;
}

interface ReviewDeliveriesResult {
    campaign?: {
        id?: string | null;
        reviewDeliveries?: {
            items?: Array<{
                destination?: string | null;
                reviewId?: string | null;
                lang?: string | null;
                origin?: string | null;
            } | null> | null;
            nextToken?: string | null;
        } | null;
    } | null;
}

// A `type` (not `interface`) so it satisfies graphqlFetch's `Record<string, unknown>` bound.
type ReviewDeliveriesVariables = {
    campaignId: string;
    nextToken?: string | null;
};

// Hand-authored (not codegen'd): the field is new and codegen can't run against a backend that
// hasn't deployed it yet. Mirrors the reportReviews paging pattern.
const reviewDeliveriesQuery = new TypedDocumentString<ReviewDeliveriesResult, ReviewDeliveriesVariables>(/* GraphQL */ `
    query CampaignReviewDeliveries($campaignId: String!, $nextToken: String) {
        campaign(campaignId: $campaignId) {
            id
            reviewDeliveries(nextToken: $nextToken) {
                items {
                    destination
                    reviewId
                    lang
                    origin
                }
                nextToken
            }
        }
    }
`);

const MAX_PAGES = 60; // safety backstop so a paging bug can't loop forever

export const useCampaignReviewDeliveries = (campaignId: string) => {
    const [deliveries, setDeliveries] = useState<ReviewDelivery[]>([]);
    // Start loading when there's a campaign to fetch, so the report's global loading gate closes
    // on the first render instead of flashing empty sections before the effect fires.
    const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(Boolean(campaignId));

    const fetchDeliveries = useCallback(async (id: string) => {
        setIsLoadingDeliveries(true);
        try {
            const byIdentity = new Map<string, ReviewDelivery>();
            let nextToken: string | null | undefined;
            let page = 0;
            do {
                const response = await graphqlFetch(reviewDeliveriesQuery, { campaignId: id, nextToken });
                const connection = response.campaign?.reviewDeliveries;
                for (const entry of connection?.items || []) {
                    if (!entry?.destination || !entry?.reviewId) {
                        continue;
                    }
                    const delivery: ReviewDelivery = {
                        destination: entry.destination,
                        reviewId: entry.reviewId,
                        lang: entry.lang ?? null,
                        origin: entry.origin ?? null,
                    };
                    const identity = [delivery.destination, delivery.reviewId, delivery.lang, delivery.origin].join(
                        "\u0000"
                    );
                    byIdentity.set(identity, delivery);
                }
                nextToken = connection?.nextToken;
                page += 1;
            } while (nextToken && page < MAX_PAGES);

            setDeliveries([...byIdentity.values()]);
        } catch (err) {
            handleError(err, { silent: true, context: { campaignId: id } });
            setDeliveries([]);
        } finally {
            setIsLoadingDeliveries(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignId) {
            setDeliveries([]);
            return;
        }
        fetchDeliveries(campaignId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    return { deliveries, isLoadingDeliveries };
};
